import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useCrearSubcontrato, useEliminarSubcontrato, useSubcontratos } from '@/hooks/useManoObra';
import { useObrasEstados } from '@/hooks/useObras';
import { useSemanas } from '@/hooks/useReportes';
import { formatearCRC } from '@adelante/shared';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

// Tipos de subcontrato (lista fija para poder tabular en reportes).
const TIPOS_SUBCONTRATO = [
  'Instalación Eléctrica',
  'Plomería / Mecánico',
  'Pintura Interior',
  'Pintura Exterior',
  'Acabados Finos',
  'Cielos',
  'Ventanas y Puertas',
  'Pisos',
  'A/C',
  'Otro',
];

export function SubcontratosPantalla() {
  const { data } = useSubcontratos();
  const semanasQ = useSemanas();
  const obrasQ = useObrasEstados();
  const crear = useCrearSubcontrato();
  const eliminar = useEliminarSubcontrato();

  const filas = data?.data ?? [];
  const semanas = semanasQ.data?.data ?? [];
  const obras = obrasQ.data?.data ?? [];
  const semNombre = useMemo(() => {
    const m = new Map<number, string>();
    for (const s of semanas) m.set(Number(s.id), `Sem ${s.numero_semana}/${s.anio}`);
    return m;
  }, [semanas]);

  const total = filas.reduce((a, f) => a + Number(f.monto), 0);

  const [abierto, setAbierto] = useState(false);
  const [semana, setSemana] = useState('');
  const [obra, setObra] = useState('');
  const [tipo, setTipo] = useState('');
  const [monto, setMonto] = useState('');
  const [desc, setDesc] = useState('');

  async function handleCrear() {
    if (!semana || !obra || !monto) return;
    try {
      await crear.mutateAsync({
        semana_operativa_id: Number(semana),
        obra_codigo: obra,
        tipo: tipo || null,
        monto: Number(monto),
        descripcion: desc || null,
      });
      toast.success('Subcontrato agregado');
      setAbierto(false);
      setObra('');
      setTipo('');
      setMonto('');
      setDesc('');
    } catch (e) {
      toast.error('No se pudo agregar', { description: e instanceof Error ? e.message : 'Error' });
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Subcontratos</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            M.O. externa por obra — se asigna directo a la obra, no se distribuye por horas.
          </p>
        </div>
        <Button onClick={() => setAbierto(true)}>+ Agregar subcontrato</Button>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
        <Kpi label="Total subcontratos" value={String(filas.length)} />
        <Kpi label="Monto total" value={formatearCRC(total)} />
      </div>

      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Semana</TableHead>
              <TableHead>Obra</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead className="text-right">Monto</TableHead>
              <TableHead>Descripción</TableHead>
              <TableHead className="text-right">Acción</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filas.map((f) => (
              <TableRow key={f.id}>
                <TableCell className="text-xs text-muted-foreground">
                  {semNombre.get(Number(f.semana_operativa_id)) ?? f.semana_operativa_id}
                </TableCell>
                <TableCell className="font-mono text-xs font-semibold">{f.obra_codigo}</TableCell>
                <TableCell className="text-sm">{f.tipo ?? '—'}</TableCell>
                <TableCell className="text-right font-mono tabular-nums">
                  {formatearCRC(Number(f.monto))}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {f.descripcion ?? '—'}
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-red-700 hover:bg-red-50"
                    disabled={eliminar.isPending}
                    onClick={() => eliminar.mutate(f.id)}
                  >
                    Eliminar
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {filas.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="py-6 text-center text-sm text-muted-foreground">
                  Sin subcontratos.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={abierto} onOpenChange={(o) => !o && setAbierto(false)}>
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle>Agregar subcontrato</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="mb-1.5 block text-xs">Semana operativa</Label>
              <Select value={semana} onValueChange={setSemana}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar…" />
                </SelectTrigger>
                <SelectContent>
                  {semanas.map((s) => (
                    <SelectItem key={s.id} value={String(s.id)}>
                      {s.anio} · Semana {s.numero_semana}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-1.5 block text-xs">Obra</Label>
              <Select value={obra} onValueChange={setObra}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar…" />
                </SelectTrigger>
                <SelectContent>
                  {obras.map((o) => (
                    <SelectItem key={o.codigo} value={o.codigo}>
                      {o.codigo}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-1.5 block text-xs">Tipo de subcontrato</Label>
              <Select value={tipo} onValueChange={setTipo}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar…" />
                </SelectTrigger>
                <SelectContent>
                  {TIPOS_SUBCONTRATO.map((t) => (
                    <SelectItem key={t} value={t}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-1.5 block text-xs">Monto pagado (₡)</Label>
              <Input
                type="number"
                value={monto}
                onChange={(e) => setMonto(e.target.value)}
                placeholder="Ej: 450000"
              />
            </div>
            <div>
              <Label className="mb-1.5 block text-xs">Descripción</Label>
              <Input
                value={desc}
                onChange={(e) => setDesc(e.target.value)}
                placeholder="Opcional"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAbierto(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCrear} disabled={!semana || !obra || !monto || crear.isPending}>
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <Card className="p-3">
      <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="text-2xl font-semibold tabular-nums">{value}</p>
    </Card>
  );
}
